import React, { useEffect, useRef, useState } from 'react';
import './StepSection.scss';
import { useDropdownScrollLock, SCROLL_FEEDBACK_CLASS } from '../../../../shared/components/ui/DropdownScroll';

import { Icon, SelectOption } from '../../../../shared/components/ui/atoms';
import { Select } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { trackEvent, QuestionFocused } from '../../../../utils/mixpanel';
import { OnboardingStep } from '../../../../types';

interface StepSectionProps {
  step: OnboardingStep;
  isSelected?: boolean;
  value?: string;
  onSelect?: (value: string) => void;
  setBottomValue?: (value: number) => void;
  isBrokerMode?: boolean;
  customMarkup?: React.ReactNode;
  stepIndex?: number;
}

export const StepSection = (props: StepSectionProps) => {
  const { step, isSelected, value, onSelect, setBottomValue, isBrokerMode, customMarkup, stepIndex } = props;
  const { iconPath, text, displayText, description, options } = step;

  const stepRef = useRef<HTMLDivElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  useDropdownScrollLock(isDropdownOpen)

  useEffect(() => {
    if (stepRef.current && isSelected) {
      setBottomValue?.(stepRef.current.offsetTop + 40)
    }
  }, [isSelected, stepRef.current])

  useEffect(() => {
    if (isSelected) {
      trackEvent(QuestionFocused(text))
    }
  }, [isSelected])

  const optionsMarkup = (
    step.isSelect
    ?
    <Select
      classNames={{ popup: { root: `state-select-dropdown ${SCROLL_FEEDBACK_CLASS}${isBrokerMode ? ' broker' : ''}` } } as any}
      value={value}
      placeholder={step.selectTitle}
      showSearch
      // Keep the down arrow while open (antd swaps it for a search icon when showSearch is on)
      suffixIcon={<DownOutlined />}
      optionFilterProp="label"
      virtual={false}
      listHeight={256}
      notFoundContent="No matching state"
      onOpenChange={setIsDropdownOpen}
      options={
        options.map(({ text, label, displayTextDropdown, isDisabled }) => ({ 
          value: text, 
          label: displayTextDropdown || label || text, 
          displayLabel: displayTextDropdown || label || text,
          disabled: isDisabled 
        }))
      }
      onChange={(value) => { onSelect?.(value) }}
    />
    :
    options.map((option) => (
      <SelectOption key={option.text} {...option} onSelect={() => onSelect?.(option.text)} isSelected={option.text === value}/>
    ))
  )

  const isPropertyLocationStep = text === 'Where is the property located?';

  return (
    <div
      className={`step-section${isPropertyLocationStep ? ' step-section-property-location' : ''}`}
      ref={stepRef}
      data-step-index={stepIndex !== undefined ? stepIndex : undefined}
    >
      <div className='icon-container'>
        <Icon svgFilePath={iconPath} isAlt={(!isSelected)} isBrokerMode={isBrokerMode} />
      </div>
      <div className='info'>
        <h1 className='title'>{displayText || text}</h1>
        {
          description
          ?
          <h3 className='description'>{description}</h3>
          :
          null
        }
      </div>
      <div className='options'>
        {
          optionsMarkup
        }
        {
          customMarkup
        }
      </div>
    </div>
  );
}